/**
 * POS-I299 (SR-35): the iPhone install handover on the page side. Pure helpers (no storage, no network) so they are unit tested.
 *
 * While the browser holds a live session on an iPhone, the page keeps a one-time code fresh in three places so whichever one the
 * "Add to Home Screen" step copies carries a valid code: the manifest address (iOS 16.4 and later read start_url from it), the
 * Safari address bar (older iOS launches the page address that was current at Add time) and the stored copy below. The installed app
 * opens with the code, removes it from its address bar at once and trades it for a session (POST /mobile-links/claim).
 */

export const HANDOVER_KEY = 'mlink.handover';
/** Mint a new code only when the current one has less than this left (a new mint replaces the old one on the server). */
export const HANDOVER_REFRESH_BELOW_MS = 20 * 60_000;
/** The page re-applies the code (manifest link, address bar) this often and whenever it becomes visible. */
export const HANDOVER_TICK_MS = 10 * 60_000;

export interface StoredHandover {
  code: string;
  /** Epoch ms. */
  expiresAt: number;
}

const CODE_RE = /^[A-Za-z0-9_-]{20,64}$/;

/** The code in an address search string (`?h=...`), or null when absent or not shaped like a code. */
export function handoverCodeFromSearch(search: string): string | null {
  try {
    const h = new URLSearchParams(search).get('h');
    return h && CODE_RE.test(h) ? h : null;
  } catch {
    return null;
  }
}

/** True when `?h` is present at all (valid or not): the page removes it from the address bar either way. */
export function hasHandoverParam(search: string): boolean {
  try {
    return new URLSearchParams(search).has('h');
  } catch {
    return false;
  }
}

export const manifestHref = (code?: string | null): string => (code ? `/m/manifest.webmanifest?h=${encodeURIComponent(code)}` : '/m/manifest.webmanifest');
export const pageHref = (code?: string | null): string => (code ? `/m?h=${encodeURIComponent(code)}` : '/m');

export function parseStoredHandover(raw: string | null): StoredHandover | null {
  if (!raw) return null;
  try {
    const o = JSON.parse(raw) as Partial<StoredHandover>;
    return typeof o.code === 'string' && CODE_RE.test(o.code) && typeof o.expiresAt === 'number' ? { code: o.code, expiresAt: o.expiresAt } : null;
  } catch {
    return null;
  }
}

/** True when a new code must be minted: none stored, or less than 20 minutes left. */
export function handoverNeedsMint(stored: StoredHandover | null, now: number): boolean {
  return !stored || stored.expiresAt - now < HANDOVER_REFRESH_BELOW_MS;
}

/** The handover only exists where it is needed: iPhone Safari in the browser (not the installed app, not Android). */
export function handoverApplies(env: { iosSafari: boolean; standalone: boolean }): boolean {
  return env.iosSafari && !env.standalone;
}
